import { flow, types } from 'mobx-state-tree'
import { ToastType } from '../../comps/Toast'
import { addUser } from '../../network/addUserPermission'
import { blockUnblockUser } from '../../network/blockUnblockUser'
import { editUser } from '../../network/editUserPermission'
import { getMasterPermissionList } from '../../network/getMasterPermissionList'
import { getUserPermissionList } from '../../network/getUserPermissionList'
import { getUsers } from '../../network/getUsers'
import { DottedActionMenuWrapper, getActionMenuComp } from '../../screens/Settings/UserManagementNew/DottedActionMenu'
import { groupBy, isOsMobile } from '../../util/util'
import { headers, tableFormattedData } from '../parser/view/userManagementTableParser'
import { permissionMapper, possibleUserPermissionManifest } from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { isRequiredPermissionPresent } from './../../util/util'
import { manageUserStatus } from './../request/userStatus'

const AccessRightSubData = types.model('AccessRightSubData', {
  text: types.string,
  isChecked: types.boolean,
})

const AccessRight = types.model('AccessRight', {
  type: types.string,
  data: types.optional(types.array(AccessRightSubData), []),
})

const permissionArray = types.model('permissionArray', {
  permissionId: types.optional(types.string, ''),
  permissionCode: types.optional(types.string, ''),
  permissionValue: types.optional(types.string, ''),
  permissionCategory: types.maybeNull(types.string),
  permissionDesc: types.maybeNull(types.string),
})

const Name = types.model('Name', {
  firstName: types.optional(types.string, ''),
  middleName: types.optional(types.string, ''),
  lastName: types.optional(types.string, ''),
})

const mobile = types.model('mobile', {
  countryCode: types.optional(types.string, ''),
  mobileNumber: types.optional(types.string, ''),
})

const individualUser = types.model('User', {
  mobile: types.optional(mobile, {}),
  name: Name,
})

const User = types.model('User', {
  id: types.optional(types.string, ''),
  email: types.optional(types.string, 'N/A'),
  department: types.optional(types.string, 'N/A'),
  createdDate: types.optional(types.string, 'N/A'),
  lastLogin: types.optional(types.string, 'N/A'),
  status: types.optional(types.string, ''),
  accessRights: types.optional(types.array(AccessRight), []),
  jfsUserId: types.optional(types.string, ''),
  entityId: types.optional(types.string, ''),
  entityType: types.optional(types.string, ''),
  accessProfileId: types.optional(types.string, ''),
  isDefault: types.optional(types.string, 'false'),
  userType: types.optional(types.string, 'false'),
  user: types.maybeNull(individualUser),
})

const UserManagement = types
  .model('Users', {
    users: types.optional(types.array(User), []),
    userPermissionData: types.maybeNull(types.array(permissionArray)),
    secondaryUserPermissonData: types.maybeNull(types.array(permissionArray)),
    masterPermissionList: types.maybeNull(types.array(permissionArray)),
    refreshTable: types.optional(types.boolean, false),
    allowedPermission: types.optional(types.array(types.string || types.undefined), []),
  })
  .actions((self) => ({
    getUsersListApi: flow(function* getUsersListApi(postData: any) {
      //FIXME: This API will fill the table data in user management.
      let response
      try {
        response = yield getUsers(RootStore.user.session.getTokens, postData)
        self.users = response
      } catch (e) {
        console.log('HTAG', e)
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    deleteUserApi: flow(function* deleteUserApi(id: any, mid: string, type: string) {
      //FIXME: This API will delete the user from table.
      let response
      try {
        response = yield blockUnblockUser(RootStore.user.session.getTokens, {
          jfsUserId: id,
          entityId: mid,
          status: manageUserStatus(type),
          statusReason: 'dummy reason',
        })
        self.users.map((item: any) => {
          item.status = item.jfsUserId === id ? 'TERMINATED' : item.status
        })
        self.refreshTable = !self.refreshTable
        RootStore?.response?.setToastType(ToastType.POSITIVE)
        RootStore?.response?.setMessage('User has been deleted successfully')
      } catch (e) {
        console.log('HTAG', e)
        RootStore?.response?.setMessage(e?.data?.error?.message ?? e?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    blockUnblockUserApi: flow(function* blockUnblockUserApi(id: any, mid: string, type: string) {
      //FIXME: This API will block/unblok the user from table.
      let response
      try {
        response = yield blockUnblockUser(RootStore.user.session.getTokens, {
          jfsUserId: id,
          entityId: mid,
          status: manageUserStatus(type),
        })
        self.refreshTable = !self.refreshTable
        RootStore?.response?.setToastType(ToastType.POSITIVE)
        RootStore?.response?.setMessage(type === 'block' ? 'User blocked successfully' : 'User activated successfully')
      } catch (e) {
        RootStore?.response?.setMessage(e?.data?.error?.message ?? e?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? e?.message ?? 'Error while processing request')
      }
      return response
    }),
    addEditUserApi: flow(function* addEditUserApi(isEditScreen: boolean, data: any) {
      let response

      if (self?.hasSupersetPermission()) {
        data?.permissionEntryList?.push({
          permissionId: '45',
          permissionValue: 'Y',
        })
        data?.permissions?.push({
          permissionId: '45',
          permissionValue: 'Y',
        })
      }
      try {
        if (!isEditScreen) {
          response = yield addUser(RootStore.user.session.getTokens, data)
        } else {
          response = yield editUser(RootStore.user.session.getTokens, data) // TODO:
        }
      } catch (e) {
        console.log('HTAG', e)
        const message = e?.data?.error?.message ?? customError.errorOccured
        RootStore?.response?.setMessage(message)
        throw new Error(message)
      }
      return response
    }),
    getUserPermissions: flow(function* getUserPermissions(data: any, isMobile: boolean, isForSelf = true) {
      //FIXME: This will give the checked values in textbox.
      let response
      try {
        response = yield getUserPermissionList(RootStore.user.session.getTokens, data, isMobile)
        // debugger
        if (isForSelf) {
          self.userPermissionData = response
        } else {
          self.secondaryUserPermissonData = response
        }
        console.log('getUserPermissionsRes', response)
        return response
      } catch (e) {
        console.log('HTAG', e)
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
    }),
    getMasterPermissionCheckboxData: flow(function* getMasterPermissionCheckboxData(data: any) {
      //FIXME: This will give the checkbox master data which is applicable for all users.
      let response
      try {
        response = yield getMasterPermissionList(RootStore.user.session.getTokens, data)
        console.log('getMasterPermissionCheckboxData', response)
        // debugger
        self.masterPermissionList = response
      } catch (e) {
        console.log('HTAG', e)
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        // self.masterPermissionList = []
        // throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    getActionMenuComp(status: any, user: any, hasSupersetPermission: boolean): any {
      if (status?.toLowerCase() === 'active') {
        return DottedActionMenuWrapper(
          [
            ...(hasSupersetPermission ? [] : [{ id: 0, text: 'Edit' }]),
            { id: 1, text: 'Block' },
            { id: 2, text: 'Delete', color: 'RED' },
          ],
          user,
        )
      } else if (status?.toLowerCase() === 'blocked') {
        return DottedActionMenuWrapper(
          [
            { id: 1, text: 'Unblock' },
            { id: 2, text: 'Delete', color: 'RED' },
          ],
          user,
        )
      } else {
        return ''
      }
    },
    refreshUserManagementTable: (val: boolean) => {
      self.refreshTable = val
    },
    notAllowedUserPermission: () => {
      let userAllowedPermission: string[] = []
      self?.userPermissionData?.map((item) => {
        permissionMapper(item, userAllowedPermission)
      })
      self.allowedPermission = userAllowedPermission
      let possibleManifest: Array<string> = possibleUserPermissionManifest.filter(
        (val: string) => !self.allowedPermission.includes(val),
      )
      return possibleManifest
      // return [] //This will over-write, use this for testing purpose.
    },
    makePreviousMerchantPermissionEmpty: (isForSelf = true) => {
      if (isForSelf) {
        self.userPermissionData = null
      } else {
        self.secondaryUserPermissonData = null
      }
    },
    getUsers(isMobile = false): any {
      let tableData = self.users
      if (!isMobile) {
        tableData = tableFormattedData(self.users, getActionMenuComp, self.hasSupersetPermission())
      }
      return self?.users?.length > 0 ? [headers, ...tableData] : []
    },
  }))
  .views((self) => ({
    // getUsers(isMobile = false): any {
    //   let tableData = self.users
    //   if (!isMobile) {
    //     tableData = tableFormattedData(self.users, self.getActionMenuComp)
    //   }
    //   return self?.users?.length > 0 ? [headers, ...tableData] : []
    // },
    getUserOptedPermissions(): any {
      const deepClone = JSON.parse(JSON.stringify(self?.masterPermissionList ?? []))
      // debugger
      const updatedMasterObjArray = deepClone.map((v) => Object.assign(v, { isChecked: false }))
      updatedMasterObjArray.forEach((data) => {
        // debugger
        self.secondaryUserPermissonData?.map((e) => {
          if (e.permissionId === data.permissionId && e?.permissionCategory === 'Y') {
            data.isChecked = true
          }
        })
      })
      const groupedElement = groupBy(updatedMasterObjArray, 'permissionCategory')
      let ACCESS_RIGHTS_API_DATA = []
      if (self.hasSupersetPermission()) {
        ACCESS_RIGHTS_API_DATA.push({
          type: 'User Management',
          data: groupedElement?.['User Management'],
          readOnly: true,
        })
      } else {
        for (const [key, value] of Object.entries(groupedElement)) {
          const MODIFIED_DATA = {
            type: key,
            data: value,
          }
          ACCESS_RIGHTS_API_DATA.push(MODIFIED_DATA)
        }
      }
      return ACCESS_RIGHTS_API_DATA
    },

    getAcessRightData(id: any): any {
      if (id) {
        return []
      }
      const newData = groupBy(self?.masterPermissionList, 'permissionCategory')
      let ACCESS_RIGHTS_API_DATA = []
      if (self.hasSupersetPermission()) {
        ACCESS_RIGHTS_API_DATA.push({
          type: 'User Management',
          data: newData?.['User Management'],
          readOnly: true,
        })
      } else {
        for (const [key, value] of Object.entries(newData)) {
          const MODIFIED_DATA = {
            type: key,
            data: value,
            readOnly: true,
          }
          ACCESS_RIGHTS_API_DATA.push(MODIFIED_DATA)
        }
      }
      return ACCESS_RIGHTS_API_DATA
    },

    isUserPermissionDataEmpty: () => {
      if (self?.userPermissionData === null) {
        return true
      }
      return false
    },

    isUserManagementEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'GET_USER_BY_ENTITY' })
    },

    canEditUserInUserManagement: () => {
      return isRequiredPermissionPresent({
        arrayName: self.userPermissionData,
        permissionName: 'UPDATE_USER_PERMISSION_LIST',
      })
    },

    canViewBlockDeleteInUserManagement: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'UPDATE_USER_STATUS' })
    },

    canUpdateUsersInUserManagement: () => {
      const isEditPermissionAvailable = isRequiredPermissionPresent({
        arrayName: self.userPermissionData,
        permissionName: 'UPDATE_USER_PERMISSION_LIST',
      })
      const blockDeletePermissionAvl = isRequiredPermissionPresent({
        arrayName: self.userPermissionData,
        permissionName: 'UPDATE_USER_STATUS',
      })
      return isEditPermissionAvailable || blockDeletePermissionAvl
    },
    isTransactionTabEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_TXN_TAB' })
    },
    isSettlementTabEnabled: () => {
      // if VIEW_SETTL_SUMMARY is enabled then show
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_SETTL_SUMMARY' })
    },
    isQrEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_QR_CODE' })
    },
    canGenerateSettlementReport: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'GEN_SETTL_REPORT' })
    },
    isRecentSettlementTabEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_SETTL_SUMMARY' })
    },
    isRecentTransactionTabEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_RECENT_TXN' })
    },
    isRecentRefundReportTabEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_RECENT_REFUND' })
    },
    isSettlementExportEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'EXPORT_SETTL_REPORT' })
    },
    canIssueRefund: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'REFUND' })
    },
    canCreatePaymentLink: () => {
      return true
    },
    canCheckPaymentLinkStatus: () => {
      return true
    },
    canExportTransationsReport: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'EXPORT_TXN_REPORT' })
    },
    isSearchBarVisible: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'SEARCH_TXN' })
    },
    canViewTransactionDetails: () => {
      return true //isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_TXN' })
    },
    toViewHighlights: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_HIGHLIGHTS' })
    },
    toViewInsights: () => {
      //TODO:
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_INSIGHTS' })
    },
    canConfigureOffers: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'CONFIG_OFFERS' })
    },
    canViewSubscriptionStatus: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_SUB_STATUS' })
    },
    isRefundedTabVisible: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_REFUND' })
    },
    isBulkRefundAvailable: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'BULK_REFUND' })
    },
    isCreateUserTabEnabled: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'ACCESS_MGMT' })
    },
    isAPICredentialsTabVisible: () => {
      return (
        isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_CRED_API' }) &&
        !self.hasSupersetPermission()
      )
    },
    canCreateCampaigns: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'CONFIG_OFFERS' })
    },
    canViewSettlementBankDetails: () => {
      return true
    },
    canViewWebHooks: () => {
      return false
    },
    canAddWebhooks: () => {
      return false
    },
    isReceivedTabVisible: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_TXN_TAB' })
    },
    canViewMobileNumber: () => {
      //Mobile number viewing is always allowed as per discussion
      // return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: '' })
      return true
    },
    canViewTransactionLimit: () => {
      return true
    },
    canExportRefundReport: () => {
      return true
    },
    canViewVaultTransactions: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'VIEW_TXN_TAB' })
    },
    canCreateSplitTransactions: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'SPLIT_PAYOUTS' })
    },
    canAddBeneficiary: () => {
      return isRequiredPermissionPresent({ arrayName: self.userPermissionData, permissionName: 'ADD_BENEFICIARIES' })
    },
    hasSupersetPermission: () => {
      return (
        false && self?.userPermissionData?.some((item) => item?.permissionCode === 'VIEW_ANALYTICS') && !isOsMobile()
      )
    },
    shouldEnableSettingForSuperset: () => {
      return self?.userPermissionData?.some((item) => ['21', '33', '35', '36', '40', '41'].includes(item?.permissionId))
    },
  }))

const UserManagementData = UserManagement.create({
  users: [],
})

export { UserManagement, UserManagementData }
